/**
 * Shop CTA banner for the wallet page.
 *
 * Lives between the bonus card and the recent-activity list. Layout:
 *
 *   Tilt the odds in your favour
 *   [Eraser tile]  [50/50 tile]  [Shop →]
 *
 * The old copy-heavy variant explained both power-ups in a paragraph;
 * now the two power-up glyphs + one-line punch per tile carry the
 * message, and each tag includes the per-live-trivia cap so users
 * know what "stock up" actually means.
 *
 * Limits shown:
 *   - Eraser: platform default `max_erasers_per_player=1` per live trivia.
 *   - 50/50: platform default `max_fiftyfifty_per_player=2` per live
 *     trivia + `FIFTYFIFTY_PER_DUEL=1` per 1-v-1 duel run.
 * Admin can override per-quiz for live but the banner is scanned in
 * seconds so we take the defaults as ground truth rather than fetching
 * per-quiz caps.
 *
 * Links to /shop. Violet→blue gradient with a pink accent edge so it
 * reads as "premium / power-up" without colliding with the wallet
 * hero's pure violet.
 */

// Stylised eraser glyph — inherits its parent tile's currentColor
// (gold). Same shape as the eraser HUD chip on /home so users see
// the same mark in both places.
const ERASER_GLYPH = `
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" stroke-width="2.2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
       style="display:block;">
    <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21"/>
    <path d="M22 21H7"/>
    <path d="m5 11 9 9"/>
  </svg>
`;

// 50/50 power-up glyph — half-filled ring. Same shape as the
// fiftyfifty HUD chip on /home.
const FIFTYFIFTY_GLYPH = `
  <svg viewBox="0 0 24 24" width="22" height="22"
       aria-hidden="true"
       style="display:block;">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2.2"/>
    <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none"/>
    <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
  </svg>
`;

// One tile per power-up. `color` sets the glyph tint (gold for eraser,
// blue for 50/50) so each mark is instantly identifiable. `caps` is a
// list of "N per surface" lines — some power-ups have one usage
// context (Eraser: live trivia only), others have two (50/50: live
// trivia + duels), so we render one cap chip per entry.
function powerupTile({ glyph, name, tag, caps, color }) {
  const capsHtml = caps
    .map(
      (c) => `
        <span style="display:block;font-size:10.5px;font-weight:600;line-height:1.4;
                     color:${color};letter-spacing:0.02em;">${c}</span>
      `,
    )
    .join('');
  return `
    <div style="flex:1 1 150px;min-width:0;display:flex;align-items:flex-start;gap:10px;
                background:rgba(255,255,255,0.04);
                border:1px solid rgba(255,255,255,0.10);
                border-radius:10px;padding:12px;">
      <span style="flex-shrink:0;color:${color};margin-top:2px;">${glyph}</span>
      <span style="min-width:0;">
        <span style="display:block;font-family:var(--font-display);font-size:13px;
                     font-weight:700;color:#fff;letter-spacing:-0.1px;">${name}</span>
        <span style="display:block;font-size:11.5px;font-weight:500;line-height:1.35;
                     color:rgba(255,255,255,0.78);margin-top:2px;">${tag}</span>
        <span style="display:flex;flex-direction:column;gap:4px;margin-top:8px;">${capsHtml}</span>
      </span>
    </div>
  `;
}

export function walletShopBannerHtml() {
  return `
    <a href="/shop" data-link class="card wallet-shop-banner"
       style="display:block;text-decoration:none;color:inherit;
              margin-top:10px;padding:14px 16px;
              background:linear-gradient(135deg, #2A1B58 0%, #15233F 70%);
              border:1px solid rgba(127,180,255,0.45);">
      <div class="row" style="align-items:center;justify-content:space-between;gap:10px;margin-bottom:10px;">
        <div style="font-family:var(--font-display);font-size:14.5px;
                    font-weight:800;color:#fff;letter-spacing:-0.1px;">
          Tilt the odds in your favour
        </div>
        <span style="flex-shrink:0;
                     padding:6px 12px;font-size:12px;font-weight:800;
                     font-family:var(--font-display);letter-spacing:0.04em;
                     background:rgba(127,180,255,0.22);
                     border:1px solid rgba(127,180,255,0.55);
                     color:#fff;border-radius:8px;white-space:nowrap;">
          Shop →
        </span>
      </div>
      <div class="row" style="align-items:stretch;gap:10px;flex-wrap:wrap;">
        ${powerupTile({
          glyph: ERASER_GLYPH,
          name: 'Eraser',
          tag: 'Auto-fixes a wrong answer',
          caps: ['Up to 1 per live trivia'],
          color: '#E5C100',
        })}
        ${powerupTile({
          glyph: FIFTYFIFTY_GLYPH,
          name: '50/50',
          tag: 'Removes 2 wrong options',
          caps: ['Up to 2 per live trivia', 'Up to 1 per 1-v-1 duel'],
          color: '#7FB4FF',
        })}
      </div>
    </a>
  `;
}
